import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const DocumentReview = () => {
    // --- STATE ---
    const [activeTab, setActiveTab] = useState('review'); // 'review', 'history', 'config'
    const [reviewDocs, setReviewDocs] = useState([]);
    const [historyDocs, setHistoryDocs] = useState([]);
    const [requirements, setRequirements] = useState([]);
    
    // --- NEW: DYNAMIC ROLES ---
    const [availableRoles, setAvailableRoles] = useState([]);

    const [expandedDocId, setExpandedDocId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Filters
    const [historyFilter, setHistoryFilter] = useState({ company: '', type: '', status: '' });

    // Config Form
    const [newReq, setNewReq] = useState({ name: '', description: '', is_required: true, approval_chain: [] });
    const [useChain, setUseChain] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        loadData();
        return () => window.removeEventListener('resize', handleResize);
    }, [activeTab]);

    const loadData = async () => {
        setLoading(true);
        try {
            // 1. Fetch Roles (Always needed for labels)
            const rolesRes = await api.get('config/roles-options/');
            setAvailableRoles(rolesRes.data);

            // 2. Fetch Tab Data
            if (activeTab === 'review') {
                const res = await api.get('documents/pending/');
                setReviewDocs(res.data);
            } else if (activeTab === 'history') {
                const res = await api.get('documents/all/');
                setHistoryDocs(res.data);
            } else {
                const res = await api.get('config/docs/');
                setRequirements(res.data);
            }
        } catch (err) { console.error("Failed to load data", err); } 
        finally { setLoading(false); }
    };

    // --- HANDLERS ---
    const handleApproval = async (docId, isApproved) => {
        try {
            const statusString = isApproved ? 'APPROVED' : 'REJECTED';
            const res = await api.post(`documents/${docId}/status/`, { status: statusString });
            if (res.data.status !== 'PENDING') {
                setReviewDocs(prev => prev.filter(d => d.id !== docId));
            } else {
                alert("✅ Signature recorded. Waiting for next approver.");
            }
        } catch (err) { alert("Action failed"); }
    };

    const handleExport = async () => {
        try {
            const params = new URLSearchParams(historyFilter).toString();
            const response = await api.get(`documents/export/?${params}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Document_Registry_${new Date().toISOString().slice(0,10)}.xlsx`);
            document.body.appendChild(link); link.click(); link.remove();
        } catch (err) { alert("Export failed"); }
    };

    // Config Handlers
    const handleAddChainStep = (roleCode) => {
        if (!roleCode) return;
        setNewReq(prev => ({ ...prev, approval_chain: [...prev.approval_chain, { role: roleCode }] }));
    };
    const handleRemoveChainStep = (index) => setNewReq(prev => ({ ...prev, approval_chain: prev.approval_chain.filter((_, i) => i !== index) }));
    
    const handleAddRequirement = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...newReq, approval_chain: useChain ? newReq.approval_chain : [] };
            await api.post('config/docs/', payload);
            setNewReq({ name: '', description: '', is_required: true, approval_chain: [] });
            setUseChain(false);
            loadData(); 
        } catch (err) { alert("Error creating requirement"); }
    };

    const handleDeleteRequirement = async (id) => {
        if(!window.confirm("Delete requirement?")) return;
        try { await api.delete(`config/docs/${id}/`); setRequirements(prev => prev.filter(r => r.id !== id)); } 
        catch (err) { alert("Error deleting"); }
    };

    // --- HELPERS ---
    const getRoleLabel = (code) => {
        const found = availableRoles.find(role => role.code === code);
        return found ? found.label : code;
    };
    
    const filteredHistory = useMemo(() => {
        return historyDocs.filter(d => {
            const matchCompany = !historyFilter.company || (d.company_name || '').toLowerCase().includes(historyFilter.company.toLowerCase());
            const matchType = !historyFilter.type || (d.doc_type || '').toLowerCase().includes(historyFilter.type.toLowerCase());
            const matchStatus = !historyFilter.status || d.status === historyFilter.status;
            return matchCompany && matchType && matchStatus;
        });
    }, [historyDocs, historyFilter]);

    // --- STYLES ---
    const inputStyle = { width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', fontSize:'0.9rem', boxSizing:'border-box' };
    const StatusBadge = ({ status }) => {
        let color = status === 'APPROVED' ? THEME.success : status === 'REJECTED' ? THEME.danger : THEME.warning;
        return <span style={{padding:'4px 10px', borderRadius:'20px', fontSize:'0.75rem', fontWeight:700, background: `${color}22`, color: color, border: `1px solid ${color}44`}}>{status}</span>
    };

    return (
        <div className="animate-fade-in">
            {/* HEADER */}
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'25px', paddingBottom:'20px', borderBottom: `1px solid ${THEME.border}`, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '10px' : '0', alignItems: isMobile ? 'flex-start' : 'center'}}>
                <div>
                    <h2 style={{color: 'white', margin:0}}>📑 Compliance Center</h2>
                    <p style={{color: THEME.textSec, margin:'5px 0 0 0', fontSize:'0.9rem'}}>Manage required documentation and approvals.</p>
                </div>
                <div style={{display:'flex', background: '#0f172a', padding:'4px', borderRadius:'8px', border: `1px solid ${THEME.border}`, width: isMobile ? '100%' : 'fit-content'}}>
                    {['review', 'history', 'config'].map(tab => (
                        <button key={tab} onClick={() => setActiveTab(tab)} style={{padding: '8px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize:'0.9rem', fontWeight:600, background: activeTab === tab ? THEME.primary : 'transparent', color: activeTab === tab ? 'white' : THEME.textSec, transition: 'all 0.2s', textTransform:'capitalize', flex: isMobile ? 1 : 'none'}}>
                            {tab === 'config' ? 'Config' : tab}
                        </button>
                    ))}
                </div>
            </div>

            {/* 1. REVIEW QUEUE */}
            {activeTab === 'review' && (
                <div className="card" style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, overflow:'hidden', minHeight:'300px', padding: isMobile ? '10px' : '0'}}>
                    {reviewDocs.length === 0 ? (
                        <div style={{textAlign:'center', padding:'60px', color: THEME.textSec}}>
                            <div style={{fontSize:'3rem', marginBottom:'10px'}}>🎉</div>
                            <h3>All caught up!</h3>
                            <p>No documents pending review.</p>
                        </div>
                    ) : (
                        isMobile ? (
                            <div style={{display:'grid', gap:'15px'}}>
                                {reviewDocs.map(d => (
                                    <div key={d.id} style={{background:'#020617', padding:'15px', borderRadius:'8px', border:`1px solid ${THEME.border}`}}>
                                        <div style={{display:'flex', justifyContent:'space-between', marginBottom:'5px'}}>
                                            <span style={{color:THEME.warning, fontWeight:'bold'}}>{d.company_name}</span>
                                            <StatusBadge status={d.status} />
                                        </div>
                                        <div style={{color:'white', marginBottom:'5px'}}>{d.doc_type}</div>
                                        <div style={{color:THEME.textSec, fontSize:'0.85rem'}}>{d.title} • {new Date(d.uploaded_at).toLocaleDateString()}</div>
                                        <div style={{marginTop:'15px', display:'flex', gap:'10px', alignItems:'center', justifyContent:'space-between', borderTop:`1px solid ${THEME.border}`, paddingTop:'15px'}}>
                                            <a href={d.file} target="_blank" rel="noopener noreferrer" style={{color:THEME.primary, fontWeight:'bold', fontSize:'0.9rem'}}>📄 View</a>
                                            <div style={{display:'flex', gap:'10px'}}>
                                                <button onClick={() => handleApproval(d.id, true)} style={{padding:'8px 16px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}>Approve</button>
                                                <button onClick={() => handleApproval(d.id, false)} style={{padding:'8px 16px', background: 'transparent', color: THEME.danger, border:`1px solid ${THEME.danger}`, borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}>Reject</button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <table style={{width: '100%', textAlign: 'left', borderCollapse: 'collapse', fontSize:'0.9rem'}}>
                                <thead style={{background:'#020617', borderBottom: `1px solid ${THEME.border}`}}>
                                    <tr>
                                        <th style={{padding:'15px', color:THEME.textSec}}>Contractor</th>
                                        <th style={{padding:'15px', color:THEME.textSec}}>Type</th>
                                        <th style={{padding:'15px', color:THEME.textSec}}>Document Title</th>
                                        <th style={{padding:'15px', color:THEME.textSec}}>Date</th>
                                        <th style={{padding:'15px', color:THEME.textSec}}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reviewDocs.map(d => (
                                        <React.Fragment key={d.id}>
                                            <tr onClick={() => setExpandedDocId(expandedDocId === d.id ? null : d.id)} style={{borderBottom: `1px solid ${THEME.border}`, cursor:'pointer', background: expandedDocId === d.id ? 'rgba(59, 130, 246, 0.1)' : 'transparent'}}>
                                                <td style={{padding:'15px', fontWeight:'bold', color: THEME.warning}}>{d.company_name}</td>
                                                <td style={{padding:'15px', color:'white'}}>{d.doc_type}</td>
                                                <td style={{padding:'15px', color: THEME.textSec}}>{d.title}</td>
                                                <td style={{padding:'15px', color: THEME.textSec}}>{new Date(d.uploaded_at).toLocaleDateString()}</td>
                                                <td style={{padding:'15px'}}><StatusBadge status={d.status} /></td>
                                            </tr>
                                            {expandedDocId === d.id && (
                                                <tr className="animate-fade-in" style={{background:'#0f172a', borderBottom:`2px solid ${THEME.primary}`}}>
                                                    <td colSpan="5" style={{padding:'20px', cursor:'default'}}>
                                                        <div style={{display:'flex', gap:'20px', alignItems:'center', justifyContent:'space-between'}}>
                                                            <div>
                                                                <a href={d.file} target="_blank" rel="noopener noreferrer" style={{display:'inline-block', padding:'10px 20px', background: THEME.card, border:`1px solid ${THEME.border}`, borderRadius:'6px', color: THEME.primary, textDecoration:'none', fontWeight:'bold', marginBottom:'5px'}}>📄 Open File Preview</a>
                                                                <div style={{fontSize:'0.85rem', color: THEME.textSec}}>Expiry: {d.expiry_date || "N/A"}</div>
                                                            </div>
                                                            <div style={{display:'flex', gap:'10px'}}>
                                                                <button onClick={() => handleApproval(d.id, true)} style={{padding:'10px 25px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}>✅ Approve</button>
                                                                <button onClick={() => handleApproval(d.id, false)} style={{padding:'10px 25px', background: 'transparent', color: THEME.danger, border:`1px solid ${THEME.danger}`, borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}>❌ Reject</button>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        )
                    )}
                </div>
            )}

            {/* 2. HISTORY */}
            {activeTab === 'history' && (
                <div className="animate-fade-in">
                    <div style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`, marginBottom:'20px', display:'flex', gap:'15px', alignItems: isMobile ? 'stretch' : 'end', flexDirection: isMobile ? 'column' : 'row'}}>
                        <div style={{flex:1}}><label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Contractor</label><input placeholder="Name..." value={historyFilter.company} onChange={e => setHistoryFilter({...historyFilter, company: e.target.value})} style={inputStyle} /></div>
                        <div style={{flex:1}}><label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Doc Type</label><input placeholder="Type..." value={historyFilter.type} onChange={e => setHistoryFilter({...historyFilter, type: e.target.value})} style={inputStyle} /></div>
                        <div style={{flex:1}}>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Status</label>
                            <select value={historyFilter.status} onChange={e => setHistoryFilter({...historyFilter, status: e.target.value})} style={inputStyle}>
                                <option value="">All</option><option value="APPROVED">Approved</option><option value="PENDING">Pending</option><option value="REJECTED">Rejected</option>
                            </select>
                        </div>
                        <button onClick={handleExport} style={{background: THEME.success, color:'white', border:'none', padding:'10px 20px', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', height:'38px'}}>📥 Excel</button>
                    </div>

                    <div style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, overflow:'hidden'}}>
                        <div style={{overflowX: 'auto'}}>
                            <table style={{width: '100%', minWidth:'700px', textAlign: 'left', borderCollapse: 'collapse', fontSize:'0.9rem'}}>
                                <thead style={{background:'#020617', borderBottom: `1px solid ${THEME.border}`}}>
                                    <tr><th style={{padding:'15px', color:THEME.textSec}}>Contractor</th><th style={{padding:'15px', color:THEME.textSec}}>Type</th><th style={{padding:'15px', color:THEME.textSec}}>Title</th><th style={{padding:'15px', color:THEME.textSec}}>Expiry</th><th style={{padding:'15px', color:THEME.textSec}}>Status</th><th style={{padding:'15px', textAlign:'right', color:THEME.textSec}}>Link</th></tr>
                                </thead>
                                <tbody>
                                    {filteredHistory.map(d => (
                                        <tr key={d.id} style={{borderBottom: `1px solid ${THEME.border}`}}>
                                            <td style={{padding:'15px', fontWeight:'bold', color: THEME.warning}}>{d.company_name}</td>
                                            <td style={{padding:'15px', color:'white'}}>{d.doc_type}</td>
                                            <td style={{padding:'15px', color: THEME.textSec}}>{d.title}</td>
                                            <td style={{padding:'15px', color: d.expiry_date ? THEME.textSec : THEME.textSec}}>{d.expiry_date || "N/A"}</td>
                                            <td style={{padding:'15px'}}><StatusBadge status={d.status} /></td>
                                            <td style={{padding:'15px', textAlign:'right'}}>{d.file && <a href={d.file} target="_blank" rel="noopener noreferrer" style={{color: THEME.primary, textDecoration:'none', fontWeight:'bold'}}>View ↗</a>}</td>
                                        </tr>
                                    ))}
                                    {filteredHistory.length === 0 && <tr><td colSpan="6" style={{padding:'30px', textAlign:'center', color: THEME.textSec}}>No documents found.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* 3. CONFIGURATION */}
            {activeTab === 'config' && (
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 350px', gap:'25px'}}>
                    
                    {/* List */}
                    <div style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, overflow:'hidden'}}>
                         <div style={{overflowX: 'auto'}}>
                             <table style={{width:'100%', minWidth:'500px', borderCollapse:'collapse', fontSize:'0.9rem'}}>
                                <thead style={{background:'#020617', borderBottom:`1px solid ${THEME.border}`}}>
                                    <tr><th style={{padding:'15px', color:THEME.textSec}}>Document Name</th><th style={{padding:'15px', color:THEME.textSec}}>Mandatory?</th><th style={{padding:'15px', color:THEME.textSec}}>Approval Flow</th><th style={{padding:'15px', textAlign:'right', color:THEME.textSec}}>Action</th></tr>
                                </thead>
                                <tbody>
                                    {requirements.map(r => (
                                        <tr key={r.id} style={{borderBottom:`1px solid ${THEME.border}`}}>
                                            <td style={{padding:'15px'}}>
                                                <div style={{color:'white', fontWeight:'bold'}}>{r.name}</div>
                                                <div style={{fontSize:'0.8rem', color:THEME.textSec}}>{r.description}</div>
                                            </td>
                                            <td style={{padding:'15px'}}>
                                                {r.is_required ? <span style={{color: THEME.danger, background:'rgba(239, 68, 68, 0.1)', padding:'2px 8px', borderRadius:'4px', fontSize:'0.75rem', fontWeight:'bold'}}>REQUIRED</span> : <span style={{color: THEME.textSec, background:'rgba(255,255,255,0.05)', padding:'2px 8px', borderRadius:'4px', fontSize:'0.75rem'}}>OPTIONAL</span>}
                                            </td>
                                            <td style={{padding:'15px', fontSize:'0.85rem', color:THEME.textSec}}>
                                                {(!r.approval_chain || r.approval_chain.length === 0) ? <span style={{opacity:0.6}}>Any Staff</span> : <div style={{display:'flex', gap:'5px', flexWrap:'wrap'}}>{r.approval_chain.map((step, idx) => (<span key={idx} style={{background: 'rgba(59, 130, 246, 0.1)', color: THEME.primary, border:`1px solid ${THEME.primary}44`, padding:'2px 6px', borderRadius:'4px'}}>{idx+1}. {getRoleLabel(step.role)}</span>))}</div>}
                                            </td>
                                            <td style={{padding:'15px', textAlign:'right'}}><button onClick={() => handleDeleteRequirement(r.id)} style={{background:'transparent', border:'none', cursor:'pointer', fontSize:'1.1rem'}}>🗑️</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Add Form */}
                    <div style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`, height:'fit-content'}}>
                        <h3 style={{marginTop:0, color: 'white'}}>➕ Add Requirement</h3>
                        <form onSubmit={handleAddRequirement} style={{display:'flex', flexDirection:'column', gap:'15px'}}>
                            <div><label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Name</label><input type="text" value={newReq.name} onChange={e => setNewReq({...newReq, name: e.target.value})} style={inputStyle} required placeholder="e.g. Insurance" /></div>
                            <div><label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Description</label><textarea rows="2" value={newReq.description} onChange={e => setNewReq({...newReq, description: e.target.value})} style={inputStyle} placeholder="Details..." /></div>
                            
                            <label style={{display:'flex', alignItems:'center', cursor:'pointer', color:'white', fontSize:'0.9rem'}}>
                                <input type="checkbox" checked={newReq.is_required} onChange={e => setNewReq({...newReq, is_required: e.target.checked})} style={{marginRight:'10px', accentColor: THEME.danger}} />
                                Mark as Mandatory
                            </label>

                            <div style={{background:'#0f172a', padding:'10px', borderRadius:'8px', border:`1px solid ${THEME.border}`}}>
                                <label style={{display:'block', color:THEME.primary, fontSize:'0.8rem', marginBottom:'10px', fontWeight:'bold'}}>Approval Workflow</label>
                                <div style={{display:'flex', gap:'5px', marginBottom:'10px'}}>
                                    <button type="button" onClick={() => setUseChain(false)} style={{flex:1, padding:'6px', borderRadius:'4px', cursor:'pointer', border: !useChain ? `1px solid ${THEME.primary}` : `1px solid ${THEME.border}`, background: !useChain ? 'rgba(59, 130, 246, 0.1)' : 'transparent', color: !useChain ? THEME.primary : THEME.textSec, fontSize:'0.8rem'}}>Any Staff</button>
                                    <button type="button" onClick={() => setUseChain(true)} style={{flex:1, padding:'6px', borderRadius:'4px', cursor:'pointer', border: useChain ? `1px solid ${THEME.primary}` : `1px solid ${THEME.border}`, background: useChain ? 'rgba(59, 130, 246, 0.1)' : 'transparent', color: useChain ? THEME.primary : THEME.textSec, fontSize:'0.8rem'}}>Chain</button>
                                </div>
                                {useChain && (
                                    <div>
                                        <div style={{marginBottom:'10px', display:'flex', flexDirection:'column', gap:'5px'}}>
                                            {newReq.approval_chain.map((step, idx) => (
                                                <div key={idx} style={{display:'flex', justifyContent:'space-between', alignItems:'center', background:'rgba(255,255,255,0.05)', padding:'5px 10px', borderRadius:'4px', fontSize:'0.8rem', color:'white'}}>
                                                    <span>{idx+1}. {getRoleLabel(step.role)}</span>
                                                    <button type="button" onClick={() => handleRemoveChainStep(idx)} style={{color: THEME.danger, background:'transparent', border:'none', cursor:'pointer'}}>✕</button>
                                                </div>
                                            ))}
                                        </div>
                                        <select onChange={(e) => { handleAddChainStep(e.target.value); e.target.value = ""; }} style={{...inputStyle, padding:'6px', fontSize:'0.8rem'}}>
                                            <option value="">+ Add Approver Role</option>
                                            {availableRoles.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                                        </select>
                                    </div>
                                )}
                            </div>

                            <button type="submit" style={{width:'100%', padding:'10px', background: THEME.primary, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Create</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentReview;